/**
 * "From the photo": what the model read from the photos and scanned pages of a case
 * (docs/vision-ocr.md §4.1). One card per picture: the file, what the picture shows,
 * a small preview, one row per value read with its reading (agreed, read once, unclear),
 * its confidence and the line it fed, and under it the sentence that nothing read from a
 * picture is accepted without a decision.
 *
 * Nothing here is shown as certain: "agreed" is information, never success, and every
 * value can be compared with the place in the picture where it was read ("Show where").
 * A picture that shows people gets no preview at all. A picture that was not read says
 * why, with the download link.
 *
 * Decision D2 (19 Oct 2026): when the mail text names the items, the other rows of a
 * pasted parts list are context. They fold away under "From the picture, not asked" and
 * feed no line. A reply's picture that only the quoted mail shows is not read again.
 *
 * Read-only display. The preview is rendered by the server from the stored mail on
 * request (`GET /cases/{id}/attachments/{index}/preview?page=&part=`); no crop is stored.
 */

import { Camera, Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapsible,
  DataGrid,
  type DataGridColumnDef,
  Dialog,
  Typography,
} from "@norican/react";
import { useCallback, useMemo, useState } from "react";

import {
  attachmentEntryHref,
  attachmentPreviewHref,
  attachmentRef,
} from "@/features/case-review/case-documents";
import { effectiveItem } from "@/features/case-review/line-decision";
import { HelpButton } from "@/features/help";
import { type AttachmentRead, type PhotoPlace, type PhotoReading } from "@/lib/api";
import { logger } from "@/lib/logger";
import { type QuoteLine } from "@/lib/mock";
import {
  frameInView,
  isHeic,
  photoIsPartly,
  photoPanelAttachments,
  type PhotoRow,
  photoRows,
  photoWasRead,
} from "@/lib/photo-reading";
import { PICTURE_WORDS } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import {
  attachmentFileText,
  fedLinesSentence,
  partText,
  PHOTO_BUDGET_SENTENCE,
  PHOTO_CHECK_MISSING_SENTENCE,
  PHOTO_COMPARE_SENTENCE,
  PHOTO_FILTER_INSTRUCTIONS_SENTENCE,
  PHOTO_FOOTER,
  PHOTO_FRAME_SENTENCE,
  PHOTO_HEIC_SENTENCE,
  PHOTO_INJECTION_SENTENCE,
  PHOTO_NO_NUMBER_SENTENCE,
  PHOTO_NO_PREVIEW_SENTENCE,
  PHOTO_PEOPLE_SENTENCE,
  PHOTO_REFUSED_SENTENCE,
  PHOTO_SEEN_IN_THREAD_SENTENCE,
  photoConfidenceText,
  photoDisagreementSentence,
  photoDroppedSentence,
  photoKindLabel,
  photoStateTag,
  photoStatusTag,
} from "@/lib/work-labels";

const LINK = "text-accent-fg-subtle w-fit text-xs font-semibold hover:underline";

/** Content filter categories that mean: the picture carries instruction-like text. */
const INSTRUCTION_CATEGORIES = new Set(["jailbreak", "indirect_attack"]);

/** What draft line n shows: "0990041 NOZZLE 12 MM"; null when unknown. */
function lineTextOf(lines: readonly QuoteLine[]): (line: number) => string | null {
  return (line) => {
    const row = lines[line - 1];
    if (!row) {
      return null;
    }
    const shown = effectiveItem(row);
    const item = shown.item ?? row.suggestions?.[0]?.item ?? null;
    return item ? [item, shown.name].filter(Boolean).join(" ") : null;
  };
}

/** The note of a picture that gave no reading; a refused reading counts as `model_rejected`. */
function notReadNote(attachment: AttachmentRead): string | null {
  return attachment.photo?.status === "rejected" && !attachment.note
    ? "model_rejected"
    : (attachment.note ?? null);
}

/** The sentence for a picture that was not read, from its note. */
function notReadSentence(attachment: AttachmentRead): string | null {
  switch (notReadNote(attachment)) {
    case "heic_not_supported":
      return PHOTO_HEIC_SENTENCE;
    case "format_not_supported":
      return isHeic(attachment.file) ? PHOTO_HEIC_SENTENCE : null;
    case "model_rejected": {
      const category = attachment.filter_category ?? attachment.photo?.filter_category ?? "";
      return INSTRUCTION_CATEGORIES.has(category.toLowerCase())
        ? PHOTO_FILTER_INSTRUCTIONS_SENTENCE
        : PHOTO_REFUSED_SENTENCE;
    }
    case "budget":
      return PHOTO_BUDGET_SENTENCE;
    default:
      return null;
  }
}

// ─── the picture ───────────────────────────────────────────────────────────

/**
 * ds-gap (image viewer, not filed yet): the design system has no image thumbnail or zoom
 * viewer, only `Avatar.Image`. Until it ships, a plain `img` element with token classes inside a
 * DS `Card` and `Dialog`, and the approximate frame as an SVG rectangle in the image's own
 * 0..1 coordinates (no inline style). Both come out when the viewer exists.
 */
function PictureImage({
  src,
  alt,
  frame,
  size,
}: {
  src: string;
  alt: string;
  frame?: [number, number, number, number] | null;
  size: "thumbnail" | "large";
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Typography variant="caption" tone="subtle">
        {PHOTO_NO_PREVIEW_SENTENCE}
      </Typography>
    );
  }
  return (
    <span className="relative inline-block w-fit max-w-full">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => {
          logger.warn("[photo] preview failed");
          setFailed(true);
        }}
        className={
          size === "thumbnail"
            ? "border-border-subtle bg-bg-subtle block max-h-80 max-w-full rounded-md border object-contain"
            : "border-border-subtle bg-bg-subtle block max-h-[70vh] max-w-full rounded-md border object-contain"
        }
      />
      {frame && (
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="text-warning-fg pointer-events-none absolute inset-0 h-full w-full"
        >
          <rect
            x={frame[0]}
            y={frame[1]}
            width={frame[2] - frame[0]}
            height={frame[3] - frame[1]}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeDasharray="6 4"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </span>
  );
}

/** What the "Show where" dialog shows: one value, or the whole picture. */
interface Shown {
  title: string;
  place: PhotoPlace | null;
}

function WhereDialog({
  caseId,
  attachment,
  photo,
  shown,
  onClose,
}: {
  caseId: string;
  attachment: AttachmentRead;
  photo: PhotoReading;
  shown: Shown | null;
  onClose: () => void;
}) {
  const place = shown?.place ?? null;
  const part = place?.part ?? null;
  const where = [
    place?.page != null ? `page ${place.page}` : null,
    partText(part, place?.parts ?? photo.parts),
  ]
    .filter(Boolean)
    .join(", ");
  const frame = frameInView(place);
  return (
    <Dialog.Root
      open={shown !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="lg">
          <Dialog.Header>
            <Dialog.Title>{shown?.title ?? attachment.file}</Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-3">
            <Dialog.Description>
              {attachment.file}
              {where ? `, ${where}` : ""}. {PHOTO_COMPARE_SENTENCE}
            </Dialog.Description>
            {photo.people_visible ? (
              <Alert tone="info">{PHOTO_PEOPLE_SENTENCE}</Alert>
            ) : (
              shown && (
                <PictureImage
                  src={attachmentPreviewHref(caseId, attachmentRef(attachment).index, {
                    child: attachmentRef(attachment).child,
                    page: place?.page ?? null,
                    part,
                  })}
                  alt={`${attachment.file}${where ? `, ${where}` : ""}`}
                  frame={frame}
                  size="large"
                />
              )
            )}
            {frame && !photo.people_visible && (
              <Typography variant="caption" tone="subtle">
                {PHOTO_FRAME_SENTENCE}
              </Typography>
            )}
            <a
              href={attachmentEntryHref(caseId, attachment)}
              download={attachment.file}
              className={LINK}
            >
              Open the file
            </a>
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── one picture ───────────────────────────────────────────────────────────

function ReadingGrid({
  rows,
  file,
  onShow,
}: {
  rows: PhotoRow[];
  file: string;
  onShow: (row: PhotoRow) => void;
}) {
  const columns = useMemo<DataGridColumnDef<PhotoRow>[]>(
    () => [
      {
        id: "what",
        header: "What",
        width: 120,
        cell: (row) => <span className="text-fg-muted block text-xs break-words">{row.what}</span>,
      },
      {
        id: "read",
        header: "Read as",
        cell: (row) => (
          <span className="block min-w-0 text-xs">
            <span className="text-fg block font-mono font-bold break-all">{row.readAs}</span>
            {row.detail && <span className="text-fg-subtle block break-words">{row.detail}</span>}
          </span>
        ),
      },
      {
        id: "reading",
        header: "Reading",
        width: 168,
        cell: (row) => {
          const tag = photoStatusTag(row.status);
          const confidence = photoConfidenceText(row.confidence);
          return (
            <span className="flex flex-col gap-0.5" data-photo-status={row.status}>
              <Badge
                tone={tag.tone}
                variant="outline"
                size="xs"
                className="h-auto w-fit whitespace-normal"
              >
                {tag.label}
              </Badge>
              {confidence && (
                <span className="text-fg-subtle text-xs tabular-nums">{confidence}</span>
              )}
            </span>
          );
        },
      },
      {
        id: "used",
        header: "Used for",
        width: 200,
        cell: (row) => (
          <span className="text-fg block text-xs break-words" data-photo-line={row.line ?? ""}>
            {row.usedFor}
          </span>
        ),
      },
      {
        id: "where",
        header: "",
        width: 104,
        cell: (row) => (
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            aria-label={`Show where ${row.what} ${row.readAs} was read in ${file}`}
            onClick={() => onShow(row)}
          >
            Show where
          </Button>
        ),
      },
    ],
    [file, onShow],
  );
  return (
    <div className="overflow-x-auto">
      <DataGrid<PhotoRow>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        density="compact"
        tableLayout="fixed"
        className="min-w-[640px]"
        aria-label={`What was read from ${file}`}
      />
    </div>
  );
}

function PictureCard({
  caseId,
  attachment,
  lines,
}: {
  caseId: string;
  attachment: AttachmentRead;
  lines: readonly QuoteLine[];
}) {
  const [shown, setShown] = useState<Shown | null>(null);
  // A refused or failed reading has no values: the card says why it was not read.
  const photo = photoWasRead(attachment.photo) ? attachment.photo : null;
  const allRows = useMemo(() => (photo ? photoRows(photo, lineTextOf(lines)) : []), [photo, lines]);
  // D2 (19 Oct 2026): the rows the mail does not ask for fold away under their own label.
  const rows = useMemo(() => allRows.filter((row) => !row.notAsked), [allRows]);
  const notAskedRows = useMemo(() => allRows.filter((row) => row.notAsked), [allRows]);
  const onShow = useCallback(
    (row: PhotoRow) => {
      logger.debug("[photo] show where", { index: attachment.index, row: row.id });
      track("case.attachment_open", { kind: "photo_where" }, { caseId });
      setShown({ title: `${row.what}: ${row.readAs}`, place: row.place });
    },
    [attachment.index, caseId],
  );
  const anyMarked = photo?.items.some((item) => item.marked) ?? false;
  const state = photoStateTag({
    hasReading: photo !== null,
    partly: photo !== null && photoIsPartly(photo),
    note: notReadNote(attachment),
    file: attachment.file,
  });
  const notRead = photo ? null : notReadSentence(attachment);
  const dropped = photo ? photoDroppedSentence(photo.dropped) : null;
  const disagreements = photo ? photoDisagreementSentence(photo.disagreements) : null;
  const noNumber =
    photo && photo.kind === "part_photo" && !photo.items.some((item) => item.code)
      ? PHOTO_NO_NUMBER_SENTENCE
      : null;
  const firstPage = attachment.kind === "image" ? null : 1;

  return (
    <Card
      variant="outline"
      padding="none"
      className="overflow-hidden"
      data-photo-card={attachment.index}
    >
      <Card.Header
        padding="sm"
        className="border-border-subtle flex flex-wrap items-center gap-2 border-b"
      >
        <a
          href={attachmentEntryHref(caseId, attachment)}
          download={attachment.file}
          className="text-accent-fg-subtle inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold hover:underline"
          onClick={() => track("case.attachment_open", { kind: "file" }, { caseId })}
        >
          <Icon icon={Camera} size="xs" />
          <span className="break-all">{attachmentFileText(attachment)}</span>
        </a>
        {photo && (
          <Badge tone="neutral" variant="outline" size="sm">
            {photoKindLabel(photo.kind, anyMarked)}
          </Badge>
        )}
        <Badge tone={state.tone} variant="subtle" size="sm" className="ml-auto">
          {state.label}
        </Badge>
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        {notRead && <Alert tone="info">{notRead}</Alert>}
        {attachment.note === "no_request" && (
          <Typography variant="body-sm" tone="muted" data-photo-no-request>
            {PICTURE_WORDS.noRequest}
          </Typography>
        )}
        {attachment.note === "seen_in_thread" && (
          <Typography variant="body-sm" tone="muted">
            {PHOTO_SEEN_IN_THREAD_SENTENCE}
          </Typography>
        )}
        {photo?.injection_suspected && <Alert tone="warning">{PHOTO_INJECTION_SENTENCE}</Alert>}
        {photo?.check_missing && <Alert tone="warning">{PHOTO_CHECK_MISSING_SENTENCE}</Alert>}

        {photo && (
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <div className="flex shrink-0 flex-col gap-1">
              {photo.people_visible ? (
                <Typography variant="caption" tone="subtle" className="max-w-60">
                  {PHOTO_PEOPLE_SENTENCE}
                </Typography>
              ) : (
                <button
                  type="button"
                  className="w-fit cursor-zoom-in"
                  aria-label={`Show the whole picture ${attachment.file}`}
                  onClick={() => {
                    logger.debug("[photo] show whole picture", { index: attachment.index });
                    track("case.attachment_open", { kind: "photo_whole" }, { caseId });
                    setShown({ title: attachment.file, place: { page: firstPage } });
                  }}
                >
                  <PictureImage
                    src={attachmentPreviewHref(caseId, attachmentRef(attachment).index, {
                      child: attachmentRef(attachment).child,
                      page: firstPage,
                      size: "thumb",
                    })}
                    alt={`Preview of ${attachment.file}`}
                    size="thumbnail"
                  />
                </button>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {noNumber && (
                <Typography variant="body-sm" className="break-words">
                  {noNumber}
                  {photo.notes ? ` ${photo.notes}` : ""}
                </Typography>
              )}
              {!noNumber && photo.notes && (
                <Typography variant="body-sm" tone="muted" className="break-words">
                  {photo.notes}
                </Typography>
              )}
              <Typography variant="caption" tone="subtle">
                {fedLinesSentence(photo.fed_lines)}
              </Typography>
              {dropped && <span className="text-warning-fg text-xs break-words">{dropped}</span>}
              {disagreements && (
                <span className="text-warning-fg text-xs break-words">{disagreements}</span>
              )}
            </div>
          </div>
        )}
        {rows.length > 0 && <ReadingGrid rows={rows} file={attachment.file} onShow={onShow} />}
        {notAskedRows.length > 0 && (
          <Collapsible.Root data-photo-not-asked={notAskedRows.length}>
            <Collapsible.Trigger className={LINK}>
              {PICTURE_WORDS.notAskedFold(notAskedRows.length)}
            </Collapsible.Trigger>
            <Collapsible.Panel className="mt-2 flex flex-col gap-2">
              <Typography variant="caption" tone="subtle">
                {PICTURE_WORDS.notAskedNote}
              </Typography>
              <ReadingGrid rows={notAskedRows} file={attachment.file} onShow={onShow} />
            </Collapsible.Panel>
          </Collapsible.Root>
        )}
        {!photo && (
          <a
            href={attachmentEntryHref(caseId, attachment)}
            download={attachment.file}
            className={LINK}
          >
            Open the file
          </a>
        )}
      </Card.Body>
      {/* The picture sentence only when I2Q read the picture (hunt round 2, Q-05): a
          picture that was not read must not say "I2Q read this from the picture". */}
      {photo && (
        <Card.Footer padding="sm" className="border-border-subtle border-t">
          <Typography variant="caption" tone="subtle" className="break-words">
            {PHOTO_FOOTER}
          </Typography>
        </Card.Footer>
      )}
      {photo && (
        <WhereDialog
          caseId={caseId}
          attachment={attachment}
          photo={photo}
          shown={shown}
          onClose={() => setShown(null)}
        />
      )}
    </Card>
  );
}

// ─── the panel ─────────────────────────────────────────────────────────────

export function PhotoPanel({
  caseId,
  attachments,
  lines,
  className,
}: {
  caseId: string;
  attachments: readonly AttachmentRead[];
  lines: readonly QuoteLine[];
  className?: string;
}) {
  const pictures = photoPanelAttachments(attachments);
  if (pictures.length === 0) {
    return null;
  }
  return (
    <section aria-label="From the photo" className={className} data-photo-panel="">
      <div className="mb-2 flex items-center gap-2">
        <Typography variant="label">From the photo</Typography>
        <Typography variant="caption" tone="subtle">
          {pictures.length} {pictures.length === 1 ? "picture" : "pictures"}
        </Typography>
        <span className="ml-auto">
          <HelpButton topic="photos" />
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {pictures.map((attachment) => (
          <PictureCard
            key={attachment.index}
            caseId={caseId}
            attachment={attachment}
            lines={lines}
          />
        ))}
      </div>
    </section>
  );
}
